import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { apiService } from "../../services/api";
import type { RootState } from "../store";

// ============ Types ============

export interface AppSettings {
  starting_year?: string;
  phone?: string;
  mobile?: string;
  email?: string;
  address?: string;
  country?: number;
  state?: number;
  city?: number;
  logo?: string;
  system_name?: string;
  currency?: number;
  header_image?: string;
  accounting_year_id?: string;
  TRN?: string;
  visa_expiration_reminder_interval?: number;
  NotificatiionPreference?: boolean | string | number | null;
  NewUpdatesNotification?: boolean | string | number | null;
  SystemNotifications?: boolean | string | number | null;
}

interface SettingsState {
  settings: AppSettings | null;
  loading: boolean;
  error: string | null;
}

// ============ Initial State ============

const initialState: SettingsState = {
  settings: null,
  loading: false,
  error: null,
};

// ============ Async Thunks ============

export const fetchSettings = createAsyncThunk(
  "settings/fetch",
  async (_, { rejectWithValue }) => {
    try {
      const response: any = await apiService.get("/pabws/api/getsettings");
      console.log("[Settings] API Response:", response);
      if (response && response.setting) {
        const settings = response.setting as AppSettings;
        console.log("[Settings] Parsed Settings:", settings);
        console.log("[Settings] NewUpdatesNotification:", settings.NewUpdatesNotification);
        console.log("[Settings] SystemNotifications:", settings.SystemNotifications);
        return settings;
      }
      console.warn("[Settings] Settings not found in response:", response);
      return rejectWithValue("Settings not found in response");
    } catch (error: any) {
      console.error("[Settings] Failed to fetch settings:", error);
      return rejectWithValue(
        error?.message || "Failed to fetch settings"
      );
    }
  }
);

// ============ Slice ============

const settingsSlice = createSlice({
  name: "settings",
  initialState,
  reducers: {
    clearSettings: (state) => {
      state.settings = null;
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchSettings.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        fetchSettings.fulfilled,
        (state, action: PayloadAction<AppSettings>) => {
          state.loading = false;
          state.settings = action.payload;
          state.error = null;
        }
      )
      .addCase(fetchSettings.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

// ============ Selectors ============

export const selectSettings = (state: RootState) =>
  state.settings.settings;

export const selectSettingsLoading = (state: RootState) =>
  state.settings.loading;

export const selectShowNotificationPreferences = (
  state: RootState
): boolean => {
  const setting = state.settings.settings?.NotificatiionPreference;
  // If settings not loaded yet, default to true (show the feature)
  if (state.settings.settings === null) return true;
  return setting === true || setting === "true";
};

export const selectShowNewUpdatesNotification = (
  state: RootState
): boolean => {
  const setting = state.settings.settings?.NewUpdatesNotification;
  // If settings not loaded yet, default to true (show the icon)
  if (state.settings.settings === null) {
    return true;
  }
  // Handle boolean, string "true", number 1, or string "1"
  if (setting === true || setting === "true") return true;
  if (typeof setting === "number" && setting === 1) return true;
  if (setting === "1") return true;
  return false;
};

export const selectShowSystemNotifications = (
  state: RootState
): boolean => {
  const setting = state.settings.settings?.SystemNotifications;
  // If settings not loaded yet, default to true (show the icon)
  if (state.settings.settings === null) {
    return true;
  }
  // Handle boolean, string "true", number 1, or string "1"
  if (setting === true || setting === "true") return true;
  if (typeof setting === "number" && setting === 1) return true;
  if (setting === "1") return true;
  return false;
};

// ============ Exports ============

export const { clearSettings } = settingsSlice.actions;
export default settingsSlice.reducer;
